import { create } from 'zustand';
import { cloneDocument, createBlankDocument, createDemoDocument } from '../model/document';
import { createId } from '../model/id';
import type {
  ModelDocument,
  ModelEntity,
  ModelSettings,
  Opening,
  SavedScene,
  ToolId,
  Vec3Tuple,
} from '../model/types';

const MAX_HISTORY = 100;

function stamp(document: ModelDocument): ModelDocument {
  return {
    ...document,
    revision: document.revision + 1,
    updatedAt: new Date().toISOString(),
  };
}

function trimHistory(history: ModelDocument[]): ModelDocument[] {
  return history.length > MAX_HISTORY ? history.slice(history.length - MAX_HISTORY) : history;
}

export type SidebarTab = 'model' | 'properties' | 'materials' | 'quantities' | 'scenes';

interface StudioState {
  document: ModelDocument;
  activeTool: ToolId;
  selectedIds: string[];
  hoveredId: string | null;
  statusMessage: string;
  cursorWorld: Vec3Tuple | null;
  sidebarTab: SidebarTab;
  sidebarOpen: boolean;
  measurementResult: number | null;
  paintColor: string;
  past: ModelDocument[];
  future: ModelDocument[];

  setActiveTool: (tool: ToolId) => void;
  setStatus: (message: string) => void;
  setCursorWorld: (position: Vec3Tuple | null) => void;
  setHoveredId: (id: string | null) => void;
  setSidebarTab: (tab: SidebarTab) => void;
  setSidebarOpen: (open: boolean) => void;
  setMeasurementResult: (metres: number | null) => void;
  setPaintColor: (color: string) => void;

  selectEntity: (id: string | null, mode?: 'replace' | 'toggle' | 'add') => void;
  addEntities: (entities: ModelEntity[]) => void;
  updateEntity: (id: string, patch: Partial<ModelEntity>) => void;
  updateEntityTransient: (id: string, patch: Partial<ModelEntity>) => void;
  commitTransient: (before: ModelDocument) => void;
  deleteEntities: (ids: string[]) => void;
  duplicateSelected: () => void;
  addOpening: (wallId: string, opening: Opening) => void;
  deleteOpening: (wallId: string, openingId: string) => void;
  updateSettings: (patch: Partial<ModelSettings>) => void;
  updateLayer: (id: string, patch: Partial<ModelDocument['layers'][number]>) => void;
  renameDocument: (name: string) => void;
  addScene: (scene: Omit<SavedScene, 'id'>) => void;
  deleteScene: (id: string) => void;
  replaceDocument: (document: ModelDocument, resetHistory?: boolean) => void;
  newBlank: () => void;
  newDemo: () => void;
  undo: () => void;
  redo: () => void;
  clearSelection: () => void;
  snapshot: () => ModelDocument;
}

export const useStudioStore = create<StudioState>((set, get) => {
  function commit(mutator: (draft: ModelDocument) => void): void {
    const current = get().document;
    const draft = cloneDocument(current);
    mutator(draft);
    const next = stamp(draft);
    set((state) => ({
      document: next,
      past: trimHistory([...state.past, current]),
      future: [],
      selectedIds: state.selectedIds.filter((id) => next.entities.some((entity) => entity.id === id)),
    }));
  }

  return {
    document: createDemoDocument(),
    activeTool: 'select',
    selectedIds: [],
    hoveredId: null,
    statusMessage: 'Ready',
    cursorWorld: null,
    sidebarTab: 'properties',
    sidebarOpen: true,
    measurementResult: null,
    paintColor: '#62d995',
    past: [],
    future: [],

    setActiveTool: (activeTool) => set({
      activeTool,
      statusMessage: toolHint(activeTool),
      measurementResult: activeTool === 'measure' ? get().measurementResult : null,
    }),
    setStatus: (statusMessage) => set({ statusMessage }),
    setCursorWorld: (cursorWorld) => set({ cursorWorld }),
    setHoveredId: (hoveredId) => set({ hoveredId }),
    setSidebarTab: (sidebarTab) => set({ sidebarTab, sidebarOpen: true }),
    setSidebarOpen: (sidebarOpen) => set({ sidebarOpen }),
    setMeasurementResult: (measurementResult) => set({ measurementResult }),
    setPaintColor: (paintColor) => set({ paintColor }),

    selectEntity: (id, mode = 'replace') => set((state) => {
      if (!id) return { selectedIds: [] };
      if (mode === 'add') {
        return { selectedIds: state.selectedIds.includes(id) ? state.selectedIds : [...state.selectedIds, id] };
      }
      if (mode === 'toggle') {
        return { selectedIds: state.selectedIds.includes(id) ? state.selectedIds.filter((value) => value !== id) : [...state.selectedIds, id] };
      }
      return { selectedIds: [id] };
    }),

    addEntities: (entities) => {
      if (entities.length === 0) return;
      commit((draft) => {
        draft.entities.push(...structuredClone(entities));
      });
      set({ selectedIds: entities.map((entity) => entity.id) });
    },

    updateEntity: (id, patch) => commit((draft) => {
      const entity = draft.entities.find((candidate) => candidate.id === id);
      if (entity) Object.assign(entity, structuredClone(patch));
    }),

    updateEntityTransient: (id, patch) => set((state) => {
      const document = cloneDocument(state.document);
      const entity = document.entities.find((candidate) => candidate.id === id);
      if (entity) Object.assign(entity, patch);
      return { document };
    }),

    commitTransient: (before) => set((state) => ({
      document: stamp(state.document),
      past: trimHistory([...state.past, before]),
      future: [],
    })),

    deleteEntities: (ids) => {
      const setOfIds = new Set(ids);
      if (setOfIds.size === 0) return;
      commit((draft) => {
        draft.entities = draft.entities.filter((entity) => !setOfIds.has(entity.id));
      });
      set((state) => ({ selectedIds: state.selectedIds.filter((id) => !setOfIds.has(id)) }));
    },

    duplicateSelected: () => {
      const { document, selectedIds } = get();
      const duplicates = document.entities
        .filter((entity) => selectedIds.includes(entity.id))
        .map((entity) => ({
          ...cloneDocument({ ...document, entities: [entity] }).entities[0],
          id: createId(entity.kind),
          name: `${entity.name} copy`,
          position: [entity.position[0] + 0.35, entity.position[1], entity.position[2] + 0.35] as Vec3Tuple,
          openings: entity.openings?.map((opening) => ({ ...opening, id: createId(opening.kind) })),
        }));
      get().addEntities(duplicates);
    },

    addOpening: (wallId, opening) => commit((draft) => {
      const wall = draft.entities.find((entity) => entity.id === wallId && entity.kind === 'wall');
      if (wall) wall.openings = [...(wall.openings ?? []), opening];
    }),

    deleteOpening: (wallId, openingId) => commit((draft) => {
      const wall = draft.entities.find((entity) => entity.id === wallId && entity.kind === 'wall');
      if (wall) wall.openings = (wall.openings ?? []).filter((opening) => opening.id !== openingId);
    }),

    updateSettings: (patch) => commit((draft) => {
      draft.settings = { ...draft.settings, ...patch };
    }),

    updateLayer: (id, patch) => commit((draft) => {
      const layer = draft.layers.find((candidate) => candidate.id === id);
      if (layer) Object.assign(layer, structuredClone(patch));
    }),

    renameDocument: (name) => commit((draft) => {
      draft.name = name.trim() || 'Untitled model';
    }),

    addScene: (scene) => commit((draft) => {
      draft.scenes.push({ ...scene, id: createId('scene') });
    }),

    deleteScene: (id) => commit((draft) => {
      draft.scenes = draft.scenes.filter((scene) => scene.id !== id);
    }),

    replaceDocument: (document, resetHistory = true) => set((state) => ({
      document: cloneDocument(document),
      selectedIds: [],
      past: resetHistory ? [] : trimHistory([...state.past, state.document]),
      future: [],
      statusMessage: `Opened ${document.name}`,
    })),

    newBlank: () => set({
      document: createBlankDocument(),
      selectedIds: [],
      past: [],
      future: [],
      statusMessage: 'New blank model',
    }),

    newDemo: () => set({
      document: createDemoDocument(),
      selectedIds: [],
      past: [],
      future: [],
      statusMessage: 'Loaded demonstration model',
    }),

    undo: () => set((state) => {
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return {
        document: cloneDocument(previous),
        past: state.past.slice(0, -1),
        future: [state.document, ...state.future].slice(0, MAX_HISTORY),
        selectedIds: state.selectedIds.filter((id) => previous.entities.some((entity) => entity.id === id)),
        statusMessage: 'Undid last action',
      };
    }),

    redo: () => set((state) => {
      const next = state.future[0];
      if (!next) return state;
      return {
        document: cloneDocument(next),
        past: trimHistory([...state.past, state.document]),
        future: state.future.slice(1),
        selectedIds: state.selectedIds.filter((id) => next.entities.some((entity) => entity.id === id)),
        statusMessage: 'Redid action',
      };
    }),

    clearSelection: () => set({ selectedIds: [] }),
    snapshot: () => cloneDocument(get().document),
  };
});

function toolHint(tool: ToolId): string {
  switch (tool) {
    case 'select': return 'Select objects. Shift-click toggles selection.';
    case 'wall': return 'Click two points to draw a semantic wall.';
    case 'room': return 'Click two opposite corners to create four walls and a slab.';
    case 'slab': return 'Click two opposite corners to draw a slab.';
    case 'box': return 'Click two opposite corners to create a box.';
    case 'line': return 'Click two points to draw a construction line.';
    case 'door': return 'Click a wall to add a hosted door opening.';
    case 'window': return 'Click a wall to add a hosted window opening.';
    case 'pushpull': return 'Drag an object vertically to change its height.';
    case 'move': return 'Select an object, then drag the transform gizmo.';
    case 'rotate': return 'Select an object, then drag a rotation ring.';
    case 'scale': return 'Select an object, then drag a scale handle.';
    case 'paint': return 'Choose a material, then click an object.';
    case 'measure': return 'Click two points to measure the distance.';
    case 'eraser': return 'Click an object to delete it.';
  }
}
